import React, { useState, useMemo } from "react";
import CustomTable from "components/Table/CustomTable";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useModal } from "contexts/ModalProvider";
import { PendingPartnersSchema } from "model/partners/pending.schema";
import Button from "reactstrap/lib/Button";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import useTableChangeHandler from "hooks/useTableChangeHandler";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import Avatar from "components/Avatar/Avatar";
import { useUpdatePartnerStatusMutation } from "services/partners/api";

const PendingPartnersTable = ({ data, error, isLoading, onTableChange }) => {
  const { handleSort, handleFilter, handleSearch, handlePageChange, forceRefresh } = useTableChangeHandler(onTableChange);

  const { modal } = useModal();
  const [selectedPendingPartners, setSelectedPendingPartners] = useState([]);
  const [actionsDropdown, setActionsDropdown] = useState({});
  const { addToast, removeToast } = useToasts();
  const [updatePartnerStatus] = useUpdatePartnerStatusMutation();

  const columns = useMemo(
    () => [
      {
        header: "Affiliate พาร์ทเนอร์",
        accessor: "firstName",
        sortable: true,
        render: (item) => (
          <div className="partner-info d-flex align-items-center gap-3">
            <Avatar src={item.profilePicture} alt={`${item.firstName} ${item.lastName}`} />
            <div>
              <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
              <div className="text-md text-gray-500">{item.email}</div>
            </div>
          </div>
        ),
      },
      {
        header: "ไอดีสมาชิก",
        accessor: "id",
        responsive: "lg",
      },
      {
        header: "วันที่สมัครสมาชิก",
        accessor: "createdAt",
        align: "center",
        responsive: "md",
        sortable: true,
        render: (item) => dayjs(item.createdAt).format("YYYY-MM-DD"),
      },
      {
        header: "เบอร์โทรศัพท์",
        accessor: "phoneNo",
        responsive: "md",
      },
      {
        header: "ข้อมูลโปรไฟล์",
        accessor: "info",
        align: "center",
        render: (item) => (
          <Link to={`/partners/${item.id}?source=pending`} className="light">
            <i className="mdi mdi-eye-outline" />
            ดูข้อมูล
          </Link>
        ),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div className="d-flex justify-content-end gap-2">
            <Button
              color="danger"
              size="sm"
              outline
              onClick={(event) => {
                event.stopPropagation();
                handleRejectPartner(item);
              }}
            >
              <i className="mdi mdi-close" />
              ซ่อน
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleApprovePartner(item);
              }}
            >
              <i className="mdi mdi-check" />
              อนุมัติ
            </Button>
          </div>
        ),
      },
    ],
    [actionsDropdown, setActionsDropdown]
  );

  const renderUser = (item) => (
    <div className="partner-info d-flex align-items-center gap-3">
      <Avatar src={item.profilePicture} alt={`${item.firstName} ${item.lastName}`} />
      <div>
        <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
        <div className="text-md text-gray-500">{item.email}</div>
      </div>
    </div>
  );

  const handleRejectPartner = async (item) => {
    const result = await modal.alert.warning({
      title: "คุณยืนยันซ่อนพาร์ทเนอร์ท่านนี้หรือไม่?",
      message: `ตรวจสอบรายชื่อพาร์ทเนอร์ที่คุณต้องการว่าถูกต้องหรือไม่? เมื่อซ่อนแล้ว รายชื่อพาร์ทเนอร์ที่ซ่อนจะไปอยู่ในแถบของ "พาร์ทเนอร์ที่ถูกซ่อน"?`,
      confirmText: "ยืนยันที่จะซ่อน",
      cancelText: "ยกเลิก",
      children: renderUser(item),
    });

    if (result) {
      try {
        await updatePartnerStatus({
          ids: [item.id],
          status: "Rejected",
          source: "Pending"
        });
        const toastId = addToast(<ToastNotification icon={check} title="ซ่อนพาร์ทเนอร์แล้ว" description={`พาร์ทเนอร์ "${item.fname} ${item.lname}" ถูกซ่อนแล้ว สามารถตรวจสอบได้ที่ "พาร์ทเนอร์ที่ถูกซ่อน"`} primaryButtonText="ดูพาร์ทเนอร์ที่ถูกซ่อน" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, {
          autoDismiss: true,
        });
        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to reject partner:", error);
        const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถซ่อนพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} variant="danger" />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleRejectAllPartner = async () => {
    const result = await modal.alert.warning({
      title: "คุณยืนยันซ่อนพาร์ทเนอร์เหล่านี้หรือไม่?",
      message: `คุณได้ทำการกดซ่อนรายการพาร์ทเนอร์ที่มีอยู่ในระบบทั้งหมด "${selectedPendingPartners.length} รายการ"`,
      confirmText: "ยืนยันที่จะซ่อน",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {selectedPendingPartners.map((partner, index) => (
            <div key={partner.id} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              {renderUser(partner)}
            </div>
          ))}
        </div>
      ),
    });

    if (result) {
      try {
        await updatePartnerStatus({
          ids: selectedPendingPartners.map((partner) => partner.id),
          status: "Rejected",
          source: "Pending",
        });
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`ซ่อนพาร์ทเนอร์แล้ว ${selectedPendingPartners.length} รายการ`} description="สามารถไปตรวจสอบพาร์ทเนอร์ที่ซ่อนไปแล้วได้ที่ พาร์ทเนอร์ที่ถูกซ่อน" primaryButtonText="ดูพาร์ทเนอร์ที่ถูกซ่อน" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to reject partners:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถซ่อนพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleApprovePartner = async (item) => {
    const result = await modal.alert.primary({
      title: "คุณยืนยันพาร์ทเนอร์ท่านนี้หรือไม่?",
      message: `ตรวจสอบรายชื่อพาร์ทเนอร์ที่คุณต้องการว่าถูกต้องหรือไม่? เมื่ออนุมัติแล้ว รายชื่อพาร์ทเนอร์ที่อนุมัติจะไปอยู่ในแถบของ "พาร์ทเนอร์ในระบบ"?`,
      confirmText: "ยืนยันที่จะอนุมัติ",
      cancelText: "ยกเลิก",
      children: renderUser(item),
    });

    if (result) {
      try {
        await updatePartnerStatus({
          ids: [item.id],
          status: "Approved",
          source: "Pending",
        });
        const toastId = addToast(<ToastNotification icon={check} title="อนุมัติพาร์ทเนอร์แล้ว" description={`พาร์ทเนอร์ "${item.fname} ${item.lname}" ถูกอนุมัติแล้ว สามารถตรวจสอบได้ที่ "พาร์ทเนอร์ในระบบ"`} primaryButtonText="ดูพาร์ทเนอร์ในระบบ" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, {
          autoDismiss: true,
        });
        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to approve partner:", error);
        const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถอนุมัติพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} variant="danger" />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleApproveAllPartner = async () => {
    const result = await modal.alert.primary({
      title: "คุณยืนยันอนุมัติพาร์ทเนอร์เหล่านี้หรือไม่?",
      message: `คุณได้ทำการกดอนุมัติรายการพาร์ทเนอร์ที่มีอยู่ในระบบทั้งหมด "${selectedPendingPartners.length} รายการ"`,
      confirmText: "ยืนยันที่จะอนุมัติ",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {selectedPendingPartners.map((partner, index) => (
            <div key={partner.id} className={`d-flex flex-column gap-2 ${index > 0 ? "pt-3" : ""}`}>
              {renderUser(partner)}
            </div>
          ))}
        </div>
      ),
    });

    if (result) {
      try {
        await updatePartnerStatus({
          ids: selectedPendingPartners.map((partner) => partner.id),
          status: "Approved",
          source: "Pending",
        });

        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`อนุมัติพาร์ทเนอร์แล้ว ${selectedPendingPartners.length} รายการ`} description="สามารถไปตรวจสอบพาร์ทเนอร์ที่อนุมัติไปแล้วได้ที่ พาร์ทเนอร์ในระบบ" primaryButtonText="ดูพาร์ทเนอร์ในระบบ" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} />, { autoDismiss: true });

        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to approve partners:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถอนุมัติพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, { autoDismiss: true });
      }
    }
  };

  return (
    <div className="table-container">
      <div className="text-lg mb-4">รายการพาร์ทเนอร์ Affiliate ที่รออนุมัติทั้งหมด *{data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={(ids) => setSelectedPendingPartners(ids)}
        selectionActions={
          <>
            <a className="success" onClick={handleApproveAllPartner}>
              <i className="bx bx-check-circle" />
              <span className="d-none d-md-block">อนุมัติ</span>
            </a>
            <a className="danger" onClick={handleRejectAllPartner}>
              <i className="bx bx-x-circle" />
              <span className="d-none d-md-block">ซ่อน</span>
            </a>
          </>
        }
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลพาร์ทเนอร์ที่รออนุมัติ"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(PendingPartnersTable, PendingPartnersSchema);

export default ValidatedTable;
